import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { SetSession } from '@/modules/auth/use-cases'

export type UseCompleteSetupPageProps = {
  onSetSession: SetSession
}

function parseHash(hash: string): { accessToken: string | null; refreshToken: string | null } {
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash
  const params = new URLSearchParams(trimmed)
  return {
    accessToken: params.get('access_token'),
    refreshToken: params.get('refresh_token'),
  }
}

function appendOrgId(target: string, orgId: string | null): string {
  if (!orgId) return target
  const separator = target.includes('?') ? '&' : '?'
  return `${target}${separator}org_id=${encodeURIComponent(orgId)}`
}

export function useCompleteSetupPage({ onSetSession }: UseCompleteSetupPageProps): void {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const startedRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const { accessToken, refreshToken } = parseHash(window.location.hash)
    const orgId = searchParams.get('org_id')

    if (!accessToken || !refreshToken) {
      navigate('/sign-in', { replace: true })
      return
    }

    void (async () => {
      try {
        await onSetSession({ accessToken, refreshToken })
        navigate(appendOrgId('/dashboard', orgId), { replace: true })
      } catch {
        navigate('/auth/auth-code-error', { replace: true })
      }
    })()
  }, [navigate, onSetSession, searchParams])
}
